/// <reference path="gLightbox.d.ts" />
/// <reference path="userData.d.ts" />
/// <reference path="config.d.ts" />
/// <reference path="gImageZoom.d.ts" />
/// <reference path="gPanels.d.ts" />

import 'sass/gLightbox.scss';

// Mock up for najax library
import {autobind} from 'core-decorators';
import AppConfig from './config';

const $ = window.jQuery;

// Detect which OS the user is accessing from
const userAgent = navigator.userAgent.toLowerCase();
const windowsPhone = userAgent.indexOf('windows phone') > -1;
const androidMobile = (userAgent.indexOf('android') > -1 && userAgent.indexOf('mobile') > -1) && !windowsPhone;
const androidTablet = (userAgent.indexOf('android') > -1 && userAgent.indexOf('mobile') === -1) && !windowsPhone;
const iPhone = (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipod') > -1) && !windowsPhone;
const iPad = userAgent.indexOf('ipad') > -1 && !windowsPhone;

// Setup basic configurations for the lightbox
const isTouchOS = (windowsPhone || androidMobile || androidTablet || iPhone || iPad);
let isOpen = false;

// Internal userId -> userName cache
let userCache = {} as Lightbox.UserCache;

/**
 * Creates a lightbox that overlaps the entire visible page, giving the user the
 * impression that he's now fully entered the contents embedded in the lightbox.
 */
class gLightbox {
    /** Helper that specifies if a lightbox is currently open */
    public static isOpen() {
        return !!isOpen;
    }

    /** Caches the original scroll-settings when the lightbox was opened */
    originalScroll: string;

    /** Specifies if the lightbox has been closed or not */
    isClosed: boolean;

    /** Contains the index of the currently shown button */
    index: number;

    /** Contains the options given when the lightbox was created */
    options: Lightbox.Options[];

    /** Contains a reference to the dom-node that wraps the entire lightbox */
    wrapper: Element;

    /** Contains references to containers previously rendered in the lightbox */
    containers: Element[];

    /** Contains references to contents previously rendered in the lightbox */
    contents: Element[];

    /** Contains cached data about all available activities */
    activities: {[propName: string]: UserData.Activity};

    /** Contains favorite-status for all learning-objects */
    favorites: {[propName: string]: boolean};

    /** Contains cached references to all bound event-listenres */
    clickListeners: {
        elem: Element,
        listener: EventListener
    }[];

    /** Indicates if we're currently navigating to another task */
    transitioning: boolean;

    /**
     * Cache options and render the lightbox when a new instance is created.
     */
    constructor(options: Lightbox.Options | Lightbox.Options[], index: number) {
        // If a lightbox is already open, then abort
        if (isOpen) {
            // Warn developers of the issue
            console.error(
                'new gLightbox(...): ' +
                'Another lightbox is already open!'
            );

            // Abort further execution
            return;
        }

        // Register that a lightbox is now open!
        isOpen = true;

        // Block scrolling on the body-layer!
        this.originalScroll = ((document.documentElement.getAttribute('css') || '').replace(/\s/g, '').split('overflow:')[1] || '').split(';')[0];
        document.documentElement.style.overflow = 'hidden';

        // Convert into array of options to support syntax that contains
        // multiple pages inside the lightbox
        this.options = Array.isArray(options) ? options : [options];
        this.index = index || 0;

        // Prepare internal cache-objects
        this.containers = new Array(this.options.length);
        this.contents = new Array(this.options.length);
        this.clickListeners = [];
        this.activities = {};
        this.favorites = {};

        // Create a wrapper-component that can contain the contents of the
        // lightbox
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'gLightbox gLightbox-interface-' + (isTouchOS ? 'touch' : 'mouse');

        // Inject the first page into the wrapper
        this.wrapper.appendChild(this.renderContainer(this.index));

        // Inject the lightbox into the DOM
        document.body.appendChild(this.wrapper);

        // Auto-close the lightbox when the user presses escape
        window.addEventListener('keyup', this.onKeyPress, true);
    }

    /**
     * Render the contents of an embedded element in the lightbox (this is
     * essentially the templating of the lightbox).
     */
    renderContainer(index: number): Element {
        // If the contents have not been rendered and cached yet, then do it now
        if (!this.contents[index]) {
            // Fetch options for rendering the page
            const options = this.options[index];

            // Create an element that can contain the contents
            const contents = document.createElement('div');

            // Apply CSS-styling
            contents.className = 'gLightbox-wrapper';

            // Render the HTML inside the lightbox
            contents.innerHTML = `
                <header class="gLightbox-header">
                    <h1 class="gLightbox-header-title">${options.title}</h1>
                    <span class="gLightbox-header-due" style="display: none;">
                        <i class="gLightbox-icon gLightbox-icon-calendar"></i>
                        <span class="gLightbox-header-due-date"></span>
                    </span>

                    <nav class="gLightbox-header-nav">
                        <a class="gLightbox-header-nav-prev gLightbox-icon gLightbox-icon-nav"></a>
                        ${index + 1} af ${this.options.length}
                        <a class="gLightbox-header-nav-next gLightbox-icon gLightbox-icon-nav"></a>
                    </nav>

                    <a class="gLightbox-header-btn gLightbox-icon gLightbox-icon-notebook"></a>
                    <a class="gLightbox-header-btn gLightbox-header-btn-throbber gLightbox-header-btn-spin gLightbox-icon gLightbox-icon-star" style="display: none;"><span class="gLightbox-header-btn-checkmarkContainer gLightbox-icon-checkmark"></span></a>
                    <a class="gLightbox-header-btn gLightbox-header-btn-throbber gLightbox-header-btn-spin gLightbox-icon gLightbox-icon-checkmark" style="display: none;"></a>
                    <a class="gLightbox-header-btn gLightbox-icon gLightbox-icon-close"></a>
                </header>

                <section class="gLightbox-contents">
                </section>
            `;

            // Create references to nested nodes
            const header = contents.getElementsByClassName('gLightbox-header')[0];
            const title = contents.getElementsByClassName('gLightbox-header-title')[0];
            const dueDate = contents.getElementsByClassName('gLightbox-header-due')[0];
            const dueDateLabel = contents.getElementsByClassName('gLightbox-header-due-date')[0];

            const nav = contents.getElementsByClassName('gLightbox-header-nav')[0];
            const navPrev = contents.getElementsByClassName('gLightbox-header-nav-prev')[0];
            const navNext = contents.getElementsByClassName('gLightbox-header-nav-next')[0];

            const btnNotebook = contents.getElementsByClassName('gLightbox-icon-notebook')[0];
            const btnFavorite = contents.getElementsByClassName('gLightbox-icon-star')[0];
            const btnSubmit = contents.getElementsByClassName('gLightbox-icon-checkmark')[1];
            const btnClose = contents.getElementsByClassName('gLightbox-icon-close')[0];

            const container = contents.getElementsByClassName('gLightbox-contents')[0];

            // Determine if this is an entry not owned by the currently signed
            // in user
            const isEntry = (options.content.type === "componentEntry" || options.content.type === "taskResult");
            const isSharedEntry = (
                (options.content.type === "componentEntry" || options.content.type === "taskResult") &&
                (options.content.userId && options.content.userId !== AppConfig.userId)
            );

            // Hide the title-element altogether, if there is no content in it
            if (!options.title && !isSharedEntry) {
                title.parentNode.removeChild(title);
            }

            // Inject username into the title (if opening an entry that's not
            // owned by the currently signed in user)
            if (isSharedEntry) {
                const entryData = <Lightbox.ComponentEntryContent | Lightbox.TaskResultContent> options.content;

                // Load name from cache?
                if (userCache[entryData.userId]) {
                    title.innerHTML = options.title ? options.title + ' | ' + userCache[entryData.userId] : userCache[entryData.userId];

                // ... Otherwise fetch name now!
                } else {
                    // Indicate that we're loading user-name
                    title.innerHTML = options.title ? options.title + ' | ...' : '...';

                    // Determine whether to load data about an eKey- or a UniC-user
                    let api = '';

                    if (!String(entryData.userId).match(/[^\@]+\@[^\.]+\..+/)) {
                        api = AppConfig.API.unicUser;
                    } else {
                        api = AppConfig.API.ekeyUser;
                    }

                    // Load data from the endpoint
                    gLightbox.ajax({
                        url: api.replace('%userId%', entryData.userId)
                    }).then(
                        (response) => {
                            try {
                                if (typeof response === 'string') {
                                    response = JSON.parse(response);
                                }

                                // Inject user-name into the lightbox
                                title.innerHTML = options.title ? options.title + ' | ' + response.Name : response.Name;

                                // Cache response
                                userCache[entryData.userId] = response.Name;
                            } catch (err) {
                                // Ignore errors...
                            }
                        },
                        () => {
                            // No specific error handling, simply not displaying
                            // username in the title...

                            // Function is added to prevent errors from being
                            // reported to the console!
                        }
                    );
                }
            }

            // Detect if there are multiple embeds in the lightbox
            if (this.options.length > 1) {
                // Bind helper-methods to navigate to the next/previous slides, when
                // the user clicks the buttons
                this.attachClickListener(
                    navPrev,
                    this.goTo.bind(this, index - 1)
                );

                this.attachClickListener(
                    navNext,
                    this.goTo.bind(this, index + 1)
                );

                // Disable if required
                if (index === 0) {
                    navPrev.classList.add('gLightbox-header-nav-disabled');
                }

                if (index === this.options.length - 1) {
                    navNext.classList.add('gLightbox-header-nav-disabled');
                }

                // Update header-state
                header.classList.add('gLightbox-header-hasNav');

            // ... Otherwise hide the navigation-bar
            } else {
                nav.parentNode.removeChild(nav);
            }

            // Determine whether to add a activity-features to the contents
            if (options.activityId) {
                btnSubmit.id = 'gLightbox-header-btn-activity-' + options.activityId;

                // Load activity-status
                this.loadActivity(options.activityId).then((activity: UserData.Activity) => {
                    // Remove the throbber
                    btnSubmit.classList.remove('gLightbox-header-btn-throbber');

                    setTimeout(() => {
                        btnSubmit.classList.remove('gLightbox-header-btn-spin');
                    }, 125);

                    // Bind callback to submit the activity
                    this.attachClickListener(
                        btnSubmit,
                        this.submitActivity.bind(this, {
                            id: options.activityId
                        })
                    );

                    // If the activity is already submitted, then update button
                    // status
                    if (activity.submitted) {
                        dueDate.classList.add('gLightbox-header-due-ok');
                        btnSubmit.classList.add('gLightbox-icon-checkmark-ok');
                    }

                    // If a due-data is defined, then inject it into the DOM
                    if (activity.due) {
                        // Parse the date
                        const due = new Date(activity.due * 1000);

                        // Prepare actual date-fragments
                        const date = due.getDate() >= 10 ? due.getDate() : '0' + due.getDate();
                        const month = due.getMonth() >= 9 ? (due.getMonth() + 1) : '0' + (due.getMonth() + 1);
                        const year = due.getFullYear() >= 10 ? due.getFullYear() : '0' + due.getFullYear();

                        // Render the due-date!
                        dueDateLabel.innerHTML = `${date}-${month}-${year}`;
                        dueDate.classList.add('gLightbox-header-due-visible');

                        // Update header-state
                        header.classList.add('gLightbox-header-hasDueDate');

                        // Highlight if the due-date has been surpassed and the
                        // activity has not been submitted yet
                        if (activity.due < Date.now() / 1000 && !activity.submitted) {
                            dueDate.classList.add('gLightbox-header-due-overdue');
                        }
                    }
                }, () => { /* Ignore errors silently */ });

            // ... Otherwise hide the button and due-date
            } else {
                btnSubmit.parentNode.removeChild(btnSubmit);
                dueDate.parentNode.removeChild(dueDate);
            }

            // Determine whether or not to add favorite-features to the lightbox
            if (options.productId && options.learningObject) {
                btnFavorite.id = 'gLightbox-header-btn-favorite-' + options.learningObject.id;

                // Load current status for the learning-object initially
                this.checkFavorite(options.learningObject).then((isFavorite: boolean) => {
                    // Remove the throbber
                    btnFavorite.classList.remove('gLightbox-header-btn-throbber');
                    setTimeout(() => {
                        btnFavorite.classList.remove('gLightbox-header-btn-spin');
                    }, 125);

                    // Bind an event-listener to toggle status on/off
                    this.attachClickListener(
                        btnFavorite,
                        this.toggleFavorite.bind(this, {
                            productId: options.productId,
                            learningObject: options.learningObject
                        })
                    );

                    if (isFavorite) {
                        btnFavorite.classList.add('gLightbox-icon-favorite-on');
                    }
                }, () => { /* Ignore errors silently */ });

            // ... Otherwise hide the button
            } else {
                btnFavorite.parentNode.removeChild(btnFavorite);
            }

            // Determine if notebook should be enabled
            if (
                (options.content.type === "componentEntry" || options.content.type === "taskResult") &&
                (options.productId && options.content.userId === AppConfig.userId)
            ) {
                // If the gPanels have been loaded, then open the notepanel on
                // click on the button instead of opening the notebook in a new
                // window
                if ($ && $(btnNotebook).gPanels) {
                    $(btnNotebook).gPanels({
                        type: 'notes',
                        iconType: 'none',
                        productId: options.productId,
                        learningObject: options.learningObject
                    });

                } else {
                    this.attachClickListener(
                        btnNotebook,
                        this.openNotebook.bind(this)
                    );
                }

            // ... Otherwise hide the button
            } else {
                btnNotebook.parentNode.removeChild(btnNotebook);
            }

            // Bind function to handle closing the lightbox
            this.attachClickListener(
                btnClose,
                this.close.bind(this)
            );

            // Render the contents inside the lightbox
            const startedAt = Date.now();
            const duration = 625;

            switch (options.content.type) {
                case 'iframe':
                case 'component':
                case 'componentEntry':
                case 'taskResult':
                    // Create an iframe that contain the desired contents
                    const iframe = document.createElement('iframe');

                    // Configure iFrame
                    iframe.id = 'gLightbox-iframe-' + index;
                    iframe.setAttribute('allowFullscreen', 'true');
                    iframe.setAttribute('allowTransparency', 'true');
                    iframe.setAttribute('frameborder', '0');

                    // Apply CSS
                    iframe.className = 'gLightbox-contents-iframe';

                    // Load contents if URL is available
                    if (options.content.type === 'iframe') {
                        // Wait for the iframe to load before being rendered
                        iframe.onload = () => {
                            container.classList.add('gLightbox-contents-loaded');
                            iframe.onload = null;
                        };

                        // Source must be given as an absolute string
                        if (typeof options.content.src === 'string') {
                            iframe.src = options.content.src as string;
                        } else {
                            throw new TypeError('gLightbox.renderContainer(...): content.src must be given as a string when content.type is "iframe"!');
                        }

                    // ... Otherwise load required data to auto-generate the
                    // URL
                    } else {
                        switch (options.content.type) {
                            case 'component':
                                // Prepare the URL that we need to forward the user to
                                let url = AppConfig.API.componentProxy + "ComponentViewer";

                                // Add authentication info to the URL
                                url += '?userID=' + encodeURIComponent(AppConfig.userId);
                                url += '&componentID=' + encodeURIComponent(options.content.componentId);
                                url += '&componentType=' + encodeURIComponent(options.content.componentType);

                                if (options.learningObject) {
                                    url += '&learningObjectId=' + encodeURIComponent(options.learningObject.id);
                                    url += '&learningObjectTitle=' + encodeURIComponent(options.learningObject.title);
                                } else {
                                    console.warn("gLightbox: Attempting to open a component without specifying learningObject. While this will work, it is strongly discouraged as it will not integrate well with AktivitetsCenter.");
                                }

                                // Wait for the iframe to load before being rendered
                                iframe.onload = () => {
                                    container.classList.add('gLightbox-contents-loaded');
                                    iframe.onload = null;
                                };

                                // Open the component in the iframe
                                iframe.src = (window as any).gProxy.forwardTo(url);

                            case 'componentEntry':
                                const content = <Lightbox.ComponentEntryContent>options.content;

                                // Fetch data about the component
                                gLightbox.ajax({url: AppConfig.API.componentEntry.replace('%entryId%', content.entryId)}).then((response) => {
                                    if (typeof response === 'string') {
                                        response = JSON.parse(response);
                                    }

                                    // Make sure everything went OK
                                    if (!response.error) {
                                        // Prepare the URL that we need to forward the user to
                                        let url = AppConfig.API.componentProxy;

                                        if (AppConfig.userId === response.userID) {
                                            url += 'ComponentViewer';
                                        } else {
                                            url += 'ComponentEntry';
                                        }

                                        // Add authentication info to the URL
                                        url += '?userID='+ encodeURIComponent(AppConfig.userId);
                                        url += '&componentID='+ encodeURIComponent(response.componentID);
                                        url += '&componentEntryID='+ encodeURIComponent(response.objectID);
                                        url += '&componentType='+ encodeURIComponent(response.componentType);

                                        if (options.learningObject) {
                                            url += '&learningObjectId=' + encodeURIComponent(options.learningObject.id);
                                            url += '&learningObjectTitle=' + encodeURIComponent(options.learningObject.title);
                                        } else {
                                            console.warn("gLightbox: Attempting to open a component without specifying learningObject. While this will work, it is strongly discouraged as it will not integrate well with AktivitetsCenter.");
                                        }

                                        // Wait for the iframe to load before being rendered
                                        iframe.onload = () => {
                                            container.classList.add('gLightbox-contents-loaded');
                                            iframe.onload = null;
                                        };

                                        // Open the component in the iframe
                                        iframe.src = (window as any).gProxy.forwardTo(url);

                                    // ... Otherwise close the window and notify the user
                                    } else {
                                        // We need to create a custom error-message that prompts the user that the
                                        // result could not be opened!
                                        this.showMessage(container, 'Der opstod en fejl under viderestilling, vi beklager...');

                                        // Kill the iframe
                                        iframe.style.display = 'none';
                                    }
                                }, () => {
                                    // Show message if data about the entry couldn't be fetched
                                    this.showMessage(container, 'Der opstod en fejl under viderestilling, vi beklager...');

                                    // Kill the iframe
                                    iframe.style.display = 'none';
                                });
                                break;

                            case 'taskResult':
                                // Fetch data about the component
                                gLightbox.ajax({url: AppConfig.API.taskResult.replace('%entryId%', options.content.entryId)}).then((response) => {
                                    if (typeof response === 'string') {
                                        response = JSON.parse(response);
                                    }

                                    // Make sure everything went OK
                                    if (!response.error) {
                                        // Prepare the URL that we need to forward the user to
                                        let url = AppConfig.API.taskPlayer;

                                        // Now add authentication to the request
                                        url += '?taskPlayerId='+ encodeURIComponent(response.taskPlayerID);
                                        url += '&userName='+ encodeURIComponent(AppConfig.userId);
                                        url += '&productID='+ encodeURIComponent(response.productID);
                                        url += '&title='+ encodeURIComponent(response.title);
                                        url += '&resultID='+ encodeURIComponent(response.objectID);

                                        // Wait for the iframe to load before being rendered
                                        iframe.onload = () => {
                                            container.classList.add('gLightbox-contents-loaded');
                                            iframe.onload = null;
                                        };

                                        // Open the taskplayer in the popup-window
                                        iframe.src = url;

                                    // ... Otherwise close the window and notify the user
                                    } else {
                                        // Show message if data about the entry couldn't be fetched
                                        this.showMessage(container, 'Der opstod en fejl under viderestilling, vi beklager...');

                                        // Kill the iframe
                                        iframe.style.display = 'none';
                                    }
                                }, () => {
                                    // Show message if data about the entry couldn't be fetched
                                    this.showMessage(container, 'Der opstod en fejl under viderestilling, vi beklager...');

                                    // Kill the iframe
                                    iframe.style.display = 'none';
                                });
                                break;

                            default:
                                // ... No other cases will ever land here
                        }
                    }

                    // Inject into the DOM
                    setTimeout(() => {
                        container.appendChild(iframe);
                    }, duration);

                    this.contents[index] = iframe;
                    break;

                case 'image':
                    // Render images without zoom-plugin?
                    if (!window.gImageZoom) {
                        // Render the wrapper for the image
                        container.innerHTML = '<div class="gLightbox-contents-image"></div>';

                        // Create an iframe that contain the desired contents
                        const wrapper = container.getElementsByClassName('gLightbox-contents-image')[0];
                        const img = new Image();

                        // Wait for the iframe to load before being rendered
                        img.onload = img.onerror = img.onabort = () => {
                            setTimeout(() => {
                                container.classList.add('gLightbox-contents-loaded');
                            }, Math.max(0, duration - (Date.now() - startedAt)));

                            img.onload = img.onerror = img.onabort = null;
                        };

                        // Apply CSS
                        img.className = 'gLightbox-contents-image-img';

                        // Source must be given as an absolute string
                        if (typeof options.content.src === 'string') {
                            img.src = options.content.src as string;
                        } else {
                            throw new TypeError('gLightbox.renderContainer(...): content.src must be given as a string when content.type is "image" and gImageZoom is not available!');
                        }

                        // Inject into the DOM
                        wrapper.appendChild(img);
                        container.appendChild(wrapper);

                        this.contents[index] = wrapper;

                    // ... Or embed into the gImageZoom component?
                    } else {
                        const content = <Lightbox.ImageContent>options.content;

                        // Render the wrapper for the zoom-plugin
                        container.innerHTML = '<div class="gLightbox-contents-image"></div>';

                        // Create a reference to the wrapper
                        const wrapper = container.getElementsByClassName('gLightbox-contents-image')[0];

                        // Initialize the gImageZoom-component!
                        setTimeout(() => {
                            container.classList.add('gLightbox-contents-loaded');

                            setTimeout(() => {
                                new window.gImageZoom({
                                    node: wrapper,
                                    src: content.src,
                                    text: content.caption,
                                });
                            });
                        }, 500);

                        this.contents[index] = wrapper;
                    }
                    break;

                default:
                    const content = <Lightbox.Content>options.content;

                    console.error(
                        `gLightbox.renderContainer(...): ` +
                        `The type "${content.type}" is not supported.`
                    );
                    break;
            }

            // Cache the page
            this.containers[index] = contents;
        }

        // Return the wrapped contents
        return this.containers[index];
    }

    /** Helper-method that applies the given listener to the click-event */
    attachClickListener(elem: Element, listener: EventListener): void {
        // Add the event-listener
        elem.addEventListener(isTouchOS ? 'touchend' : 'click', listener, false);

        // Cache callback
        this.clickListeners.push({elem, listener});
    }

    /**
     * Renders a message inside the container, which is useful if an error
     * occurs while trying to download the contents.
     */
    showMessage(container: Element, message: string): void {
        // Create DOM-elements
        const wrapper = document.createElement('div');
        const label = document.createElement('span');

        wrapper.appendChild(label);

        // Apply styling
        wrapper.className = 'gLightbox-contents-message';
        label.className = 'gLightbox-contents-message-label';

        // Inject message
        label.innerHTML = message;

        // Inject into the wrapper
        container.appendChild(wrapper);

        // Make sure that the message is displayed
        container.classList.add('gLightbox-contents-loaded');
    }

    /**
     * Navigates to the specified index of available content pages in the
     * lightbox.
     */
    goTo(index: number): void {
        index = Math.max(0, Math.min(this.options.length - 1, index));

        // Skip if we're already at this index
        if (this.index === index || this.transitioning) {
            return;
        }

        this.transitioning = true;

        // Prepare leave for the currently rendered page
        const oldContainer = this.containers[this.index];
        const oldContents = this.contents[this.index];

        const newContainer = this.renderContainer(index);
        const newContents = this.contents[index];

        // Reset current animations
        oldContainer.classList.remove('gLightbox-wrapper-transition-in-left');
        oldContainer.classList.remove('gLightbox-wrapper-transition-in-right');
        oldContainer.classList.remove('gLightbox-wrapper-transition-out-left');
        oldContainer.classList.remove('gLightbox-wrapper-transition-out-right');

        newContainer.classList.remove('gLightbox-wrapper-transition-in-left');
        newContainer.classList.remove('gLightbox-wrapper-transition-in-right');
        newContainer.classList.remove('gLightbox-wrapper-transition-out-left');
        newContainer.classList.remove('gLightbox-wrapper-transition-out-right');

        // Slide left or right?
        if (index > this.index) {
            oldContainer.classList.add('gLightbox-wrapper-transition-out-left');
            newContainer.classList.add('gLightbox-wrapper-transition-in-left');
        } else {
            oldContainer.classList.add('gLightbox-wrapper-transition-out-right');
            newContainer.classList.add('gLightbox-wrapper-transition-in-right');
        }

        // If rendering an iframe, then remove it temporarily while we're
        // transitioning to prevent framerate-drop!
        const handleIframe = (
            newContents.tagName.toLowerCase() === 'iframe' &&
            newContents.parentNode
        );

        if (handleIframe) {
            (newContents.parentNode as Element).classList.remove('gLightbox-contents-loaded');
            newContents.parentNode.removeChild(newContents);
        }

        this.wrapper.appendChild(newContainer);

        // Remove the old child after the animation has finished
        setTimeout(() => {
            // Remove the old container from the DOM
            oldContainer.parentNode.removeChild(oldContainer);

            // Re-inject iframes into the lightbox, and wait for it to load
            if (handleIframe) {
                // Wait for the frame to load before removing the throbber
                (newContents as HTMLIFrameElement).onload = () => {
                    // Remove the throbber
                    newContainer.getElementsByClassName('gLightbox-contents')[0].classList.add('gLightbox-contents-loaded');

                    // Clear event-callback!
                    (newContents as HTMLIFrameElement).onload = null;
                };

                // Reload iframe
                newContainer.getElementsByClassName('gLightbox-contents')[0].appendChild(newContents);
            }

            // Register that transitioning has finished
            this.transitioning = false;
        }, 625);

        // Update current index
        this.index = index;
    }

    /** Auto-close the lightbox if the user clicks escape */
    @autobind
    onKeyPress(evt: KeyboardEvent): void {
        // Close if the user presses escape
        if (evt.keyCode === 27) {
            this.close();
        }

        // Navigate prev/next if the user uses the arrows
        if (evt.keyCode === 39) {
            this.goTo(this.index + 1);
        } else if (evt.keyCode === 37) {
            this.goTo(this.index - 1);
        }

        // Stop event propagation
        if (evt.stopPropagation) {
            evt.stopPropagation();
        }

        evt.cancelBubble = true;
    }

    /** Closes the lightbox and completely cleans up memory after it */
    close(): void {
        // Prevent accidentally triggering the closing-mechanism twice
        if (this.isClosed) {
            return;
        }

        this.isClosed = true;

        // Run the close-animation
        this.wrapper.classList.add('gLightbox-closing');

        // Unbind all associated event-listeners
        this.clickListeners.forEach((click) => {
            click.elem.removeEventListener(isTouchOS ? 'touchend' : 'click', click.listener, false);
        });

        // Clean up memory
        this.options = null;
        this.containers = null;
        this.contents = null;
        this.activities = null;
        this.favorites = null;
        this.clickListeners = null;

        // Wait for the animation to finish, then remove the lightbox from the
        // DOM
        setTimeout(() => {
            this.wrapper.parentNode.removeChild(this.wrapper);
            this.wrapper = null;

            // Re-allow scrolling
            document.documentElement.style.overflow = this.originalScroll;

            // Register that the lightbox was closed
            isOpen = false;
        }, 625);

        // Unbind event-listeners
        window.removeEventListener('keyup', this.onKeyPress, true);
    }

    /** Helper-method that loads activity data from the UserData API */
    async loadActivity(id: string): Promise<UserData.Activity> {
        // If this activity has already been loaded, then return the internally
        // cached data!
        if (this.activities[id]) {
            return this.activities[id];
        }

        // Cache starting timestamp (we want at least 500ms to go by, before the
        // throbber is hidden)
        const startedAt = Date.now();
        const minDuration = 650;

        await new Promise((resolve) => {
            setTimeout(resolve, Math.random() * 2000);
        });

        // ... Otherwise load the activity!
        const activity = {
            id,
            due: Math.round(Date.now() / 1000 + (Math.random() - 0.5) * 5184000),
            submitted: Math.random() >= 0.5
        };

        // Cache it internally
        this.activities[id] = activity;

        // Wait the minimum duration before updating the UI
        await new Promise((resolve) => {
            setTimeout(resolve, Math.max(0, minDuration - (Date.now() - startedAt)));
        });

        // Return data
        return activity;
    }

    /** Helper-method that submits an activity using the UserData API */
    async submitActivity({id, allowRetry = true}: {id: string, allowRetry: boolean}): Promise<boolean> {
        // Abort, if already submitted
        if (this.activities[id].submitted) {
            return true;
        }

        // Cache starting timestamp (we want at least 500ms to go by, before the
        // throbber is hidden)
        const startedAt = Date.now();
        const minDuration = 500 + Math.round(200 * Math.random());

        // Find references to DOM-nodes
        const btn = document.getElementById('gLightbox-header-btn-activity-' + id);
        const due = (btn.parentNode as Element).getElementsByClassName('gLightbox-header-due')[0];

        // Show throbber while submitting
        btn.classList.add('gLightbox-header-btn-throbber');
        btn.classList.add('gLightbox-header-btn-spin');

        // Perform AJAX-request
        try {
            await new Promise((resolve) => {
                setTimeout(resolve, Math.random() * 1000);
            });

            // Handle errors?
            if (Math.random() >= 0.5) {
                throw new Error('fail');
            }
        } catch (err) {
            // Retry request?
            if (allowRetry) {
                setTimeout(() => {
                    this.submitActivity({id, allowRetry: false});
                }, Math.round((Math.random() + 0.5) * 500));

                return false;
            }

            // Alert the user
            alert(`Der opstod en fejl, da vi forsøgte at aflevere aktiviteten til din lærer.`);

            // Hide the throbber
            btn.classList.remove('gLightbox-header-btn-throbber');
            setTimeout(() => {
                btn.classList.remove('gLightbox-header-btn-spin');
            }, 125);
            return false;
        }

        // Update status
        this.activities[id].submitted = true;

        // Wait the minimum duration before updating the UI
        await new Promise((resolve) => {
            setTimeout(resolve, Math.max(0, minDuration - (Date.now() - startedAt)));
        });

        // Update button-status
        btn.classList.add('gLightbox-icon-checkmark-ok');
        due.classList.add('gLightbox-header-due-ok');
        due.classList.remove('gLightbox-header-due-overdue');

        // Hide throbber again
        btn.classList.remove('gLightbox-header-btn-throbber');
        setTimeout(() => {
            btn.classList.remove('gLightbox-header-btn-spin');
        }, 125);

        // Assume that the activity was submitted, otherwise return an error
        return true;
    }

    /**
     * Helper-method that determines if a learning-object is currently marked
     * as a favorite by the user.
     */
    async checkFavorite(learningObject: UserData.LearningObject): Promise<boolean> {
        // If this learningObject status has already been loaded, then return
        // the internally cached data!
        if (this.favorites[learningObject.id]) {
            return this.favorites[learningObject.id];
        }

        let isFavorite = false;

        try {
            await gLightbox.ajax({
                url: AppConfig.API.checkFavorite.replace('%id%', encodeURIComponent(learningObject.id))
            });

            // If the object was found, then mark as favorite
            isFavorite = true;
        } catch (ignore) {
            // If an error occurred during the request, that means the object
            // has not been marked as favorite - so just continue, doing nothing
        }

        // Cache favorite-status internally
        this.favorites[learningObject.id] = isFavorite;

        // Return result
        return this.favorites[learningObject.id];
    }

    /** Helper-method that toggles favorite-status on/off */
    async toggleFavorite({productId, learningObject, allowRetry = true}: {productId: string, learningObject: UserData.LearningObject, allowRetry: boolean}): Promise<boolean> {
        // Cache starting timestamp (we want at least 500ms to go by, before the
        // throbber is hidden)
        const startedAt = Date.now();
        const minDuration = 500 + Math.round(200 * Math.random());

        // Cache reference to DOM-node
        const btn = document.getElementById('gLightbox-header-btn-favorite-' + learningObject.id);

        // Abort if we're already working!
        if (allowRetry && (btn.classList.contains('gLightbox-header-btn-throbber') || btn.classList.contains('gLightbox-header-btn-checkmark'))) {
            return false;
        }

        // Show throbber while submitting
        btn.classList.add('gLightbox-header-btn-throbber');
        btn.classList.add('gLightbox-header-btn-spin');

        // Add to favorite?
        if (!this.favorites[learningObject.id]) {
            try {

                await gLightbox.ajax({
                    url: AppConfig.API.addFavorite,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    data: JSON.stringify({
                        userId: AppConfig.userId,
                        productId,
                        learningObject
                    })
                });

            // If the request fails, then warn the user
            } catch (err) {
                // Retry request?
                if (allowRetry) {
                    setTimeout(() => {
                        this.toggleFavorite({productId, learningObject, allowRetry: false});
                    }, Math.round((Math.random() + 0.5) * 500));

                    return false;
                }

                // Alert the user
                alert(`Der opstod en fejl, da vi forsøgte at tilføje "${learningObject.title}" til dine favoritter.`);

                // Hide the throbber
                btn.classList.remove('gLightbox-header-btn-throbber');
                setTimeout(() => {
                    btn.classList.remove('gLightbox-header-btn-spin');
                }, 125);
                return false;
            }

        // ... Or remove favorite?
        } else {
            try {
                await gLightbox.ajax({
                    url: AppConfig.API.removeFavorite.replace('%id%', encodeURIComponent(learningObject.id)),
                    method: 'DELETE'
                });

            // If the request fails, then warn the user
            } catch (err) {
                // Retry request?
                if (allowRetry) {
                    setTimeout(() => {
                        this.toggleFavorite({productId, learningObject, allowRetry: false});
                    }, Math.round((Math.random() + 0.5) * 500));

                    return false;
                }

                // Alert the user
                alert(`Der opstod en fejl, da vi forsøgte at fjerne "${learningObject.title}" fra dine favoritter.`);

                // Hide the throbber
                btn.classList.remove('gLightbox-header-btn-throbber');
                setTimeout(() => {
                    btn.classList.remove('gLightbox-header-btn-spin');
                }, 125);
                return false;
            }
        }

        // Toggle favorite on/off
        this.favorites[learningObject.id] = !this.favorites[learningObject.id];

        // Wait the minimum duration before updating the UI
        await new Promise((resolve) => {
            setTimeout(resolve, Math.max(0, minDuration - (Date.now() - startedAt)));
        });

        // Update class
        if (this.favorites[learningObject.id]) {
            btn.classList.add('gLightbox-icon-favorite-on');
        } else {
            btn.classList.remove('gLightbox-icon-favorite-on');
        }

        // Hide throbber again
        btn.classList.remove('gLightbox-header-btn-throbber');
        setTimeout(() => {
            btn.classList.remove('gLightbox-header-btn-spin');
        }, 125);

        // Show a checkmark for the next two seconds
        btn.classList.add('gLightbox-header-btn-favorite-updated');
        btn.classList.add('gLightbox-header-btn-checkmark');

        setTimeout(() => {
            btn.classList.remove('gLightbox-header-btn-favorite-updated');
            btn.classList.remove('gLightbox-header-btn-checkmark');
        }, 1500);

        // Assume that the favorite-status was toggled correctly
        return true;
    }

    /**
     * Helper-method that opens the notebook associated with the currently
     * embedded contents in a new window.
     */
    openNotebook(): void {
        const options = this.options[this.index];

        // Generate base URL
        let url = AppConfig.API.notebook.replace('%id%', encodeURIComponent(options.productId));

        // Append context to URL
        if (options.learningObject) {
            url += '&context-learningObjectId='+ encodeURIComponent(options.learningObject.id);
            url += '&context-learningObjectTitle='+ encodeURIComponent(options.learningObject.title);
            url += '&context-learningObjectType='+ encodeURIComponent(options.learningObject.type);
        }

        // Forward through the node.js proxy to ensure authentication
        url = (window as any).gProxy.forwardTo(url, 'notebook-3.0');

        // // Open in a new window that's centered around the current window
        window.open(url, 'notebook', 'left='+ (window.screenLeft + 50) +',top='+ (window.screenTop + 50) +',width='+ (window.innerWidth - 100) +',height='+ (window.innerHeight - 100));
    }

    /** Internal helper that converts nanoajax-requests into a promise */
    static ajax (params: JQuery.AjaxSettings): Promise<any> {
        return $.ajax(params);
    }
};

// Expose class publicly
(window as any).gLightbox = gLightbox;



// WEBPACK FOOTER //
// ./js/gLightbox.ts